import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  format, 
  addMinutes, 
  addDays, 
  startOfWeek, 
  endOfWeek, 
  isSameDay,
  isToday,
  isBefore,
  startOfDay
} from "date-fns";
import { 
  ArrowLeft, 
  CheckCircle2, 
  Clock, 
  User, 
  Calendar,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Plus,
  Minus,
  ListChecks,
  Phone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const appointmentTypes = [
  { value: "evaluation", label: "Evaluation", duration: 30 },
  { value: "supervisory", label: "Supervisory", duration: 15 },
];

const disciplines = [
  { value: "PT", label: "Physical Therapy (PT)" },
  { value: "OT", label: "Occupational Therapy (OT)" },
  { value: "SLP", label: "Speech Language Pathology (SLP)" },
];

const states = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const BookAppointment = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [weeklyAvailability, setWeeklyAvailability] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [step, setStep] = useState(1);
  
  // Waitlist booking state
  const [waitlistId, setWaitlistId] = useState(null);
  const [isFromWaitlist, setIsFromWaitlist] = useState(false);

  // Waitlist CONFIRM dialog state — the "Join Waitlist" buttons no longer
  // fire the API immediately; they open this dialog so the facilitator
  // can review/edit contact info, needed-by date, and notes first.
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [waitlistDraft, setWaitlistDraft] = useState({
    contact_info: "",
    preferred_day: "",
    notes: "",
  });
  
  // Multiple visit types state
  const [evalCount, setEvalCount] = useState(0);
  const [supCount, setSupCount] = useState(0);
  const [stEvalCount, setStEvalCount] = useState(0);
  const [stTreatCount, setStTreatCount] = useState(0);
  
  // Calculate total duration based on selected visit types
  const totalDuration = (evalCount * 30) + (supCount * 15) + (stEvalCount * 45) + (stTreatCount * 30);
  
  // Generate visit type summary
  const getVisitTypeSummary = () => {
    const parts = [];
    if (evalCount > 0) parts.push(`${evalCount} Eval${evalCount > 1 ? 's' : ''} (${evalCount * 30} min)`);
    if (supCount > 0) parts.push(`${supCount} Sup${supCount > 1 ? 's' : ''} (${supCount * 15} min)`);
    if (stEvalCount > 0) parts.push(`${stEvalCount} ST Eval${stEvalCount > 1 ? 's' : ''} (${stEvalCount * 45} min)`);
    if (stTreatCount > 0) parts.push(`${stTreatCount} ST Treat${stTreatCount > 1 ? 's' : ''} (${stTreatCount * 30} min)`);
    return parts.join(' + ');
  };
  
  const [formData, setFormData] = useState({
    patient_name: "",
    appointment_type: "",
    duration: 30,
    required_discipline: "",
    state: "",
    location: "",
    selected_slot: null,
    preferred_day: "",  // For waitlist - preferred day for appointment
    contact_info: "",   // Patient contact information (phone/email)
    notes: "",          // Optional notes (preferred therapist, accommodations, etc.)
  });

  // Pre-fill form from URL params (from waitlist)
  useEffect(() => {
    const waitlistIdParam = searchParams.get('waitlist_id');
    const patientName = searchParams.get('patient_name');
    const discipline = searchParams.get('discipline');
    const state = searchParams.get('state');
    const duration = searchParams.get('duration');
    const location = searchParams.get('location');
    const visitType = searchParams.get('visit_type');
    
    if (waitlistIdParam && patientName) {
      setWaitlistId(waitlistIdParam);
      setIsFromWaitlist(true);
      
      // Parse duration to set eval/sup counts
      const durationNum = parseInt(duration) || 30;
      // Assume evaluations for simplicity, or parse visit_type
      if (visitType && visitType.toLowerCase().includes('sup')) {
        setSupCount(Math.ceil(durationNum / 15));
      } else {
        setEvalCount(Math.ceil(durationNum / 30));
      }
      
      setFormData(prev => ({
        ...prev,
        patient_name: patientName,
        required_discipline: discipline || "",
        state: state || "",
        location: location || "",
        duration: durationNum,
      }));
    }
  }, [searchParams]);

  // Update duration when eval/sup counts change
  useEffect(() => {
    if (totalDuration > 0) {
      setFormData(prev => ({ ...prev, duration: totalDuration }));
    }
  }, [totalDuration]);

  // Generate week days from current week start
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  // Fetch weekly availability when discipline, state, or duration changes
  useEffect(() => {
    if (formData.required_discipline && formData.state && step === 2 && totalDuration > 0) {
      fetchWeeklyAvailability();
    }
  }, [formData.required_discipline, formData.state, totalDuration, step, currentWeekStart]);

  const fetchWeeklyAvailability = async () => {
    setLoadingSlots(true);
    try {
      const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
      const res = await axios.get(`${API}/availability/week`, {
        params: {
          discipline: formData.required_discipline,
          state: formData.state,
          week_start: currentWeekStart.toISOString(),
          week_end: weekEnd.toISOString(),
          duration: formData.duration,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setWeeklyAvailability(res.data.availability || {});
      // Clear selected slot when availability changes
      setFormData(prev => ({ ...prev, selected_slot: null }));
    } catch (error) {
      console.error("Failed to fetch weekly availability:", error);
      setWeeklyAvailability({});
    } finally {
      setLoadingSlots(false);
    }
  };

  const handlePreviousWeek = () => {
    const newStart = addDays(currentWeekStart, -7);
    // Don't allow going to past weeks
    if (isBefore(newStart, startOfDay(new Date()))) {
      setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
    } else {
      setCurrentWeekStart(newStart);
    }
    setSelectedDate(null);
    setFormData({ ...formData, selected_slot: null });
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
    setSelectedDate(null);
    setFormData({ ...formData, selected_slot: null });
  };

  const handleSelectDate = (date) => {
    // Don't allow selecting past dates
    if (isBefore(date, startOfDay(new Date()))) return;
    setSelectedDate(date);
    setFormData({ ...formData, selected_slot: null });
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.patient_name.trim()) {
        toast.error("Please enter facilitator name");
        return;
      }
      if (totalDuration === 0) {
        toast.error("Please select at least one visit type");
        return;
      }
      if (!formData.required_discipline) {
        toast.error("Please select a discipline");
        return;
      }
      if (!formData.state) {
        toast.error("Please select a state");
        return;
      }
      if (!formData.location.trim()) {
        toast.error("Please enter facility location");
        return;
      }
      if (!formData.contact_info.trim()) {
        toast.error("Please enter patient contact information (phone or email)");
        return;
      }
      // Build appointment type string from all selected visit types
      const typeParts = [];
      if (evalCount > 0) typeParts.push("evaluation");
      if (supCount > 0) typeParts.push("supervisory");
      if (stEvalCount > 0) typeParts.push("st_evaluation");
      if (stTreatCount > 0) typeParts.push("st_treatment");
      const apptType = typeParts.join(" + ");
      setFormData(prev => ({ ...prev, appointment_type: apptType }));
    }
    if (step === 2 && !formData.selected_slot) {
      toast.error("Please select an available time slot");
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    if (step === 1) {
      navigate("/facilitator");
    } else {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const slot = formData.selected_slot;
      const windowStart = new Date(slot.start_time);
      const windowEnd = addMinutes(windowStart, formData.duration);
      
      // Get the browser's timezone for email formatting
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      await axios.post(
        `${API}/appointments`,
        {
          patient_name: formData.patient_name,
          visit_type: formData.appointment_type,
          required_discipline: formData.required_discipline,
          state: formData.state,
          location: formData.location,
          contact_info: formData.contact_info,  // Patient contact information
          notes: formData.notes || null,
          window_start: windowStart.toISOString(),
          window_end: windowEnd.toISOString(),
          duration_minutes: formData.duration,
          availability_block_id: slot.block_id,
          timezone: browserTimezone,
          waitlist_id: waitlistId, // Include waitlist_id if booking from waitlist
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (isFromWaitlist) {
        toast.success("Appointment booked from waitlist! Patient has been removed from waitlist.");
      } else {
        toast.success("Appointment booked successfully! Confirmation email sent.");
      }
      navigate("/facilitator");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to book appointment");
    } finally {
      setLoading(false);
    }
  };

  const openWaitlistDialog = () => {
    // Basic guards: without a patient/discipline/state the waitlist entry
    // is worthless. Force the facilitator back up-form to fix.
    if (!formData.patient_name?.trim()) {
      toast.error("Patient name is required before joining the waitlist");
      return;
    }
    if (!formData.required_discipline || !formData.state) {
      toast.error("Please fill discipline and state before joining the waitlist");
      return;
    }
    // Seed the dialog from the main form. The facilitator can adjust
    // contact info, needed-by, and notes before actually submitting.
    setWaitlistDraft({
      contact_info: formData.contact_info || "",
      preferred_day: formData.preferred_day || "",
      notes: formData.notes || "",
    });
    setWaitlistDialogOpen(true);
  };

  const handleAddToWaitlist = async () => {
    try {
      setLoading(true);
      const draftNotes = waitlistDraft.notes?.trim();
      const notesForPayload = draftNotes
        ? `Needs ${totalDuration} minutes. ${draftNotes}`
        : `Requested by facilitator when no slots were available. Needs ${totalDuration} minutes.`;
      const response = await axios.post(
        `${API}/waitlist`,
        {
          patient_name: formData.patient_name,
          required_discipline: formData.required_discipline,
          state: formData.state,
          visit_type: getVisitTypeSummary(),
          duration: totalDuration,
          location: formData.location,
          notes: notesForPayload,
          contact_info: waitlistDraft.contact_info?.trim() || null,
          preferred_day: waitlistDraft.preferred_day || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Persist the confirmed values back into the main form so the
      // summary panel and any re-open of the dialog reflect them.
      setFormData((prev) => ({
        ...prev,
        contact_info: waitlistDraft.contact_info,
        preferred_day: waitlistDraft.preferred_day,
        notes: draftNotes || prev.notes,
      }));

      setWaitlistDialogOpen(false);
      toast.success(`Added to waitlist! ${response.data.therapists_notified} therapist(s) have been notified.`);
      navigate("/facilitator");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to waitlist");
    } finally {
      setLoading(false);
    }
  };

  // Check if a slot can accommodate the full duration
  const canSlotFitDuration = (slot) => {
    if (!slot.block_end_time) return true; // Fallback for old data
    const slotStart = new Date(slot.start_time);
    const blockEnd = new Date(slot.block_end_time);
    const appointmentEnd = addMinutes(slotStart, totalDuration);
    return appointmentEnd <= blockEnd;
  };

  const handleSelectSlot = (slot) => {
    // Validate that the slot can accommodate the full duration
    if (!canSlotFitDuration(slot)) {
      const blockEnd = new Date(slot.block_end_time);
      toast.error(
        `This slot cannot fit a ${totalDuration}-minute appointment. The therapist's availability ends at ${format(blockEnd, "h:mm a")}. Please select an earlier time or join the waitlist.`
      );
      return;
    }
    setFormData({ ...formData, selected_slot: slot });
  };

  const formatTime = (dateStr) => {
    return format(new Date(dateStr), "h:mm a");
  };

  const formatDateHeader = (date) => {
    return format(date, "EEE, MMM d");
  };

  // Get slots for selected date
  const selectedDateKey = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;
  const slotsForSelectedDate = selectedDateKey ? (weeklyAvailability[selectedDateKey] || []) : [];

  // Group slots by therapist
  const groupedSlots = slotsForSelectedDate.reduce((acc, slot) => {
    if (!acc[slot.therapist_name]) {
      acc[slot.therapist_name] = [];
    }
    acc[slot.therapist_name].push(slot);
    return acc;
  }, {});

  // Check if any day in the week has availability
  const hasAnyAvailability = Object.values(weeklyAvailability).some(slots => slots && slots.length > 0);

  // Get count of available slots per day (only slots that can fit the duration)
  const getSlotCount = (date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const slots = weeklyAvailability[dateKey] || [];
    return slots.filter(s => !s.booked && canSlotFitDuration(s)).length;
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          {step === 1 ? "Back to Dashboard" : "Previous Step"}
        </button>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors ${
                  step >= s
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div
                  className={`w-16 h-1 mx-1 ${step > s ? "bg-primary" : "bg-muted"}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Patient Info & Appointment Type */}
        {step === 1 && (
          <Card>
            <CardHeader>
              {isFromWaitlist && (
                <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                  <ListChecks className="w-4 h-4" />
                  <span className="text-sm font-medium">Booking from Waitlist</span>
                </div>
              )}
              <CardTitle className="font-['Manrope']">
                {isFromWaitlist ? "Complete Waitlist Booking" : "Schedule Patient Appointment"}
              </CardTitle>
              <CardDescription>
                {isFromWaitlist 
                  ? "Patient information has been pre-filled from the waitlist. Select an available time slot."
                  : "Enter patient information and appointment details"
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Facilitator Information */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Facilitator Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="patient_name">Facilitator Name *</Label>
                    <Input
                      id="patient_name"
                      placeholder="Jane Smith"
                      value={formData.patient_name}
                      onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                      data-testid="facilitator-name-input"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Appointment Type */}
              <div className="space-y-4">
                <h3 className="font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Appointment Details
                </h3>
                
                <div className="space-y-2">
                  <Label>Visit Types *</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Select the number of each visit type needed for this appointment
                  </p>
                  
                  {/* Evaluation Counter */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Evaluations</p>
                      <p className="text-sm text-muted-foreground">30 minutes each</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setEvalCount(Math.max(0, evalCount - 1))}
                        disabled={evalCount === 0}
                        data-testid="eval-minus"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-semibold text-lg">{evalCount}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setEvalCount(evalCount + 1)}
                        data-testid="eval-plus"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Supervisory Counter */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">Supervisory Visits</p>
                      <p className="text-sm text-muted-foreground">15 minutes each</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setSupCount(Math.max(0, supCount - 1))}
                        disabled={supCount === 0}
                        data-testid="sup-minus"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-semibold text-lg">{supCount}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setSupCount(supCount + 1)}
                        data-testid="sup-plus"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* ST Evaluation Counter */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">ST Evaluation</p>
                      <p className="text-sm text-muted-foreground">45 minutes each</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setStEvalCount(Math.max(0, stEvalCount - 1))}
                        disabled={stEvalCount === 0}
                        data-testid="st-eval-minus"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-semibold text-lg">{stEvalCount}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setStEvalCount(stEvalCount + 1)}
                        data-testid="st-eval-plus"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* ST Treatment Counter */}
                  <div className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <p className="font-medium">ST Treatment</p>
                      <p className="text-sm text-muted-foreground">30 minutes each</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setStTreatCount(Math.max(0, stTreatCount - 1))}
                        disabled={stTreatCount === 0}
                        data-testid="st-treat-minus"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-8 text-center font-semibold text-lg">{stTreatCount}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setStTreatCount(stTreatCount + 1)}
                        data-testid="st-treat-plus"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Total Duration Display */}
                  {totalDuration > 0 && (
                    <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg mt-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-primary">Total Appointment Duration</p>
                          <p className="text-sm text-muted-foreground">{getVisitTypeSummary()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-primary">{totalDuration} min</p>
                          {totalDuration >= 60 && (
                            <p className="text-sm text-muted-foreground">
                              ({Math.floor(totalDuration / 60)}h {totalDuration % 60 > 0 ? `${totalDuration % 60}m` : ''})
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Required Discipline *</Label>
                  <Select
                    value={formData.required_discipline}
                    onValueChange={(value) => setFormData({ ...formData, required_discipline: value })}
                  >
                    <SelectTrigger data-testid="discipline-select">
                      <SelectValue placeholder="Select discipline" />
                    </SelectTrigger>
                    <SelectContent>
                      {disciplines.map((d) => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Patient State *</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value })}
                    >
                      <SelectTrigger data-testid="state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Facility Location *</Label>
                    <Input
                      id="location"
                      placeholder="Main Campus"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      data-testid="location-input"
                    />
                  </div>
                </div>
                
                {/* Contact Information */}
                <div className="space-y-2">
                  <Label htmlFor="contact_info">
                    Contact Information <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    id="contact_info"
                    placeholder="Phone number, email, or other contact details for the patient"
                    value={formData.contact_info}
                    onChange={(e) => setFormData({ ...formData, contact_info: e.target.value })}
                    required
                    data-testid="contact-info-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Required: Phone number, email, or any information needed to contact the patient
                  </p>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="appointment_notes">Notes</Label>
                  <textarea
                    id="appointment_notes"
                    placeholder="Preferred therapist, accommodations, or any other details the therapist should know…"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    data-testid="appointment-notes-input"
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <p className="text-xs text-muted-foreground">
                    Optional: e.g. preferred therapist name, hearing aid, interpreter required.
                  </p>
                </div>
              </div>

              <Button onClick={handleNext} className="w-full" data-testid="step1-next-btn">
                View Available Times
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Weekly Calendar View */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope'] flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                Select Appointment Date & Time
              </CardTitle>
              <CardDescription>
                {disciplines.find(d => d.value === formData.required_discipline)?.label} availability in {formData.state}
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                  {formData.duration}-minute slots
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Week Navigation */}
              <div className="flex items-center justify-between">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handlePreviousWeek}
                  disabled={isBefore(currentWeekStart, startOfDay(new Date()))}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <span className="font-medium">
                  {format(currentWeekStart, "MMM d")} - {format(addDays(currentWeekStart, 6), "MMM d, yyyy")}
                </span>
                <Button variant="outline" size="sm" onClick={handleNextWeek}>
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {loadingSlots ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : (
                <>
                  {/* Mobile: dropdown picker (7-col grid is too cramped on phones) */}
                  <div className="md:hidden">
                    <Label htmlFor="mobile-date-picker" className="text-sm mb-2 block">Pick a day</Label>
                    <select
                      id="mobile-date-picker"
                      data-testid="mobile-date-picker"
                      value={selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""}
                      onChange={(e) => {
                        const picked = weekDays.find(d => format(d, "yyyy-MM-dd") === e.target.value);
                        if (picked) handleSelectDate(picked);
                      }}
                      className="w-full h-12 px-3 rounded-md border border-input bg-background text-base"
                    >
                      <option value="">— Select a day —</option>
                      {weekDays.map((date) => {
                        const dateKey = format(date, "yyyy-MM-dd");
                        const slotCount = getSlotCount(date);
                        const isPast = isBefore(date, startOfDay(new Date()));
                        if (isPast) return null;
                        return (
                          <option key={dateKey} value={dateKey}>
                            {format(date, "EEE, MMM d")}{isToday(date) ? " (Today)" : ""} — {slotCount > 0 ? `${slotCount} slot${slotCount === 1 ? "" : "s"}` : "no slots"}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  {/* Desktop: 7-day grid */}
                  <div className="hidden md:grid grid-cols-7 gap-2">
                    {weekDays.map((date) => {
                      const dateKey = format(date, "yyyy-MM-dd");
                      const slotCount = getSlotCount(date);
                      const isPast = isBefore(date, startOfDay(new Date()));
                      const isSelected = selectedDate && isSameDay(date, selectedDate);
                      const hasSlots = slotCount > 0;

                      return (
                        <button
                          key={dateKey}
                          onClick={() => handleSelectDate(date)}
                          disabled={isPast}
                          className={`p-3 rounded-lg border text-center transition-all ${
                            isPast
                              ? "bg-muted/50 text-muted-foreground cursor-not-allowed opacity-50"
                              : isSelected
                              ? "border-primary bg-primary text-primary-foreground"
                              : hasSlots
                              ? "border-emerald-300 bg-emerald-50 hover:border-emerald-400"
                              : "border-border hover:border-muted-foreground/50"
                          }`}
                          data-testid={`day-${dateKey}`}
                        >
                          <p className="text-xs font-medium">{format(date, "EEE")}</p>
                          <p className={`text-lg font-bold ${isToday(date) ? "underline" : ""}`}>
                            {format(date, "d")}
                          </p>
                          {!isPast && (
                            <p className={`text-xs mt-1 ${
                              isSelected ? "text-primary-foreground/80" : 
                              hasSlots ? "text-emerald-600" : "text-muted-foreground"
                            }`}>
                              {hasSlots ? `${slotCount} slots` : "No slots"}
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-6 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded border-2 border-emerald-300 bg-emerald-50" />
                      <span className="text-muted-foreground">Available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded border bg-muted/50" />
                      <span className="text-muted-foreground">No availability</span>
                    </div>
                  </div>

                  {/* Time Slots for Selected Day */}
                  {selectedDate && (
                    <div className="mt-6 pt-6 border-t">
                      <h3 className="font-semibold mb-4">
                        Available times for {formatDateHeader(selectedDate)}
                      </h3>

                      {Object.keys(groupedSlots).length === 0 ? (
                        <div className="text-center py-8 bg-muted/30 rounded-lg">
                          <AlertCircle className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                          <p className="text-muted-foreground font-medium">No available slots on {format(selectedDate, "MMMM d")}</p>
                          <p className="text-sm text-muted-foreground mt-1 mb-4">
                            Try selecting another day or join the waitlist
                          </p>
                          <Button 
                            variant="default"
                            onClick={openWaitlistDialog}
                            className="bg-amber-600 hover:bg-amber-700"
                            disabled={loading}
                            data-testid="add-to-waitlist-day-btn"
                          >
                            {loading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Clock className="w-4 h-4 mr-2" />
                            )}
                            Join Waitlist
                          </Button>
                          <p className="text-xs text-muted-foreground mt-3">
                            We'll notify therapists licensed in {formData.state}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {Object.entries(groupedSlots).map(([therapistName, slots]) => (
                            <div key={therapistName} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                <span className="font-medium">{therapistName}</span>
                                <Badge variant="outline">{formData.required_discipline}</Badge>
                              </div>
                              <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 pl-10">
                                {slots.map((slot, idx) => {
                                  const canFit = canSlotFitDuration(slot);
                                  const isDisabled = slot.booked || !canFit;
                                  const isSelected = formData.selected_slot?.block_id === slot.block_id && 
                                                     formData.selected_slot?.start_time === slot.start_time;
                                  
                                  return (
                                    <button
                                      key={`${slot.block_id}-${idx}`}
                                      type="button"
                                      onClick={() => handleSelectSlot(slot)}
                                      disabled={isDisabled}
                                      title={!canFit ? `Cannot fit ${totalDuration} min appointment - block ends at ${formatTime(slot.block_end_time)}` : ''}
                                      className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                                        isSelected
                                          ? "border-primary bg-primary text-primary-foreground"
                                          : slot.booked
                                          ? "border-muted bg-muted/50 text-muted-foreground cursor-not-allowed line-through"
                                          : !canFit
                                          ? "border-amber-300 bg-amber-50 text-amber-600 cursor-not-allowed"
                                          : "border-border hover:border-primary/50"
                                      }`}
                                      data-testid={`time-slot-${formatTime(slot.start_time).replace(/[: ]/g, '-')}`}
                                    >
                                      {formatTime(slot.start_time)}
                                      {!canFit && !slot.booked && (
                                        <span className="block text-xs opacity-75">too late</span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Show message if there are slots but none can fit the duration */}
                      {Object.keys(groupedSlots).length > 0 && 
                       !slotsForSelectedDate.some(s => !s.booked && canSlotFitDuration(s)) && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-amber-800">
                                No time slots can fit your {totalDuration}-minute appointment
                              </p>
                              <p className="text-sm text-amber-700 mt-1">
                                The available slots would extend past the therapist's availability. 
                                Try selecting an earlier day, reducing the appointment duration, or join the waitlist.
                              </p>
                              <Button 
                                variant="outline"
                                size="sm"
                                onClick={openWaitlistDialog}
                                className="mt-3 border-amber-400 text-amber-700 hover:bg-amber-100"
                                disabled={loading}
                              >
                                <Clock className="w-4 h-4 mr-2" />
                                Join Waitlist
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No availability this week message with waitlist option */}
                  {!hasAnyAvailability && !selectedDate && (
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="font-semibold text-lg mb-2">No availability this week</h3>
                      <p className="text-muted-foreground mb-4">
                        There are no {formData.required_discipline} therapists licensed in {formData.state} 
                        {" "}with availability this week.
                      </p>

                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Button variant="outline" onClick={handleNextWeek}>
                          Check Next Week
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                        <Button 
                          variant="default"
                          onClick={openWaitlistDialog}
                          className="bg-amber-600 hover:bg-amber-700"
                          data-testid="add-to-waitlist-btn"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Join Waitlist
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-4">
                        You'll confirm the patient's details (contact, needed-by date, notes) before submitting.
                        Once submitted, therapists licensed in {formData.state} will be notified.
                      </p>
                    </div>
                  )}

                  {/* Selected Slot Summary */}
                  {formData.selected_slot && (
                    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                      <p className="text-sm font-medium">
                        Selected: {selectedDate && formatDateHeader(selectedDate)} at {formatTime(formData.selected_slot.start_time)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        with {formData.selected_slot.therapist_name} • {totalDuration} minutes
                      </p>
                    </div>
                  )}
                </>
              )}

              <Button 
                onClick={handleNext} 
                className="w-full" 
                disabled={!formData.selected_slot}
                data-testid="step2-next-btn"
              >
                Review Booking
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-['Manrope']">Review Appointment</CardTitle>
              <CardDescription>
                Please confirm the appointment details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-secondary/50 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Facilitator</p>
                    <p className="font-medium">{formData.patient_name}</p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Visit Types</p>
                    <p className="font-medium">{getVisitTypeSummary()}</p>
                    <p className="text-sm text-muted-foreground">
                      Total: {totalDuration} minutes • {disciplines.find(d => d.value === formData.required_discipline)?.label}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date & Time</p>
                    <p className="font-medium">
                      {selectedDate && format(selectedDate, "EEEE, MMMM d, yyyy")} at {formData.selected_slot && formatTime(formData.selected_slot.start_time)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      with {formData.selected_slot?.therapist_name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{formData.location}, {formData.state}</p>
                  </div>
                </div>
                
                {formData.contact_info && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Contact Information</p>
                      <p className="font-medium">{formData.contact_info}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Confirmation:</strong> An email confirmation will be sent to you ({user?.email}) 
                  for your records. The patient information is stored for session tracking.
                </p>
              </div>

              <Button 
                onClick={handleSubmit} 
                className="w-full" 
                disabled={loading}
                data-testid="confirm-booking-btn"
              >
                {loading ? "Booking..." : "Confirm Appointment"}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Confirm Waitlist Request dialog — replaces the previous
          "auto-submit" behavior. The facilitator can review/edit the
          fields the therapists will actually see before it's sent. */}
      <Dialog open={waitlistDialogOpen} onOpenChange={setWaitlistDialogOpen}>
        <DialogContent
          className="max-w-lg"
          data-testid="waitlist-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle>Confirm Waitlist Request</DialogTitle>
            <DialogDescription>
              Review the details below. Therapists licensed in{" "}
              <strong>{formData.state}</strong> for <strong>{formData.required_discipline}</strong>{" "}
              will be notified as soon as you submit.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Read-only summary of what will be sent */}
            <div className="rounded-md border border-muted bg-muted/30 p-3 text-sm space-y-1">
              <div>
                <span className="text-muted-foreground">Patient:</span>{" "}
                <span className="font-medium">{formData.patient_name || "—"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Discipline / Visit:</span>{" "}
                <span className="font-medium">
                  {formData.required_discipline} · {getVisitTypeSummary() || "—"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Duration:</span>{" "}
                <span className="font-medium">{totalDuration} minutes</span>
              </div>
              <div>
                <span className="text-muted-foreground">State / Location:</span>{" "}
                <span className="font-medium">
                  {formData.state}
                  {formData.location ? ` · ${formData.location}` : ""}
                </span>
              </div>
            </div>

            {/* Editable fields */}
            <div className="space-y-2">
              <Label htmlFor="wl-contact">
                Patient contact info <span className="text-muted-foreground">(phone or email)</span>
              </Label>
              <Input
                id="wl-contact"
                value={waitlistDraft.contact_info}
                onChange={(e) =>
                  setWaitlistDraft((d) => ({ ...d, contact_info: e.target.value }))
                }
                placeholder="555-123-4567 or patient@example.com"
                data-testid="waitlist-contact-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-needed-by">
                Needed by <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="wl-needed-by"
                type="date"
                value={waitlistDraft.preferred_day}
                onChange={(e) =>
                  setWaitlistDraft((d) => ({ ...d, preferred_day: e.target.value }))
                }
                min={new Date().toISOString().split("T")[0]}
                data-testid="waitlist-needed-by-input"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank for &ldquo;as soon as possible&rdquo;.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wl-notes">
                Notes for the therapist <span className="text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="wl-notes"
                value={waitlistDraft.notes}
                onChange={(e) =>
                  setWaitlistDraft((d) => ({ ...d, notes: e.target.value }))
                }
                placeholder="Any specifics — preferred days/times, patient background, urgency, etc."
                rows={4}
                data-testid="waitlist-notes-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setWaitlistDialogOpen(false)}
              disabled={loading}
              data-testid="waitlist-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToWaitlist}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="waitlist-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Clock className="w-4 h-4 mr-2" />
                  Submit to Waitlist
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default BookAppointment;
